import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  CLEAR_OFFER_CHECK_IN_CHECK_OUT,
  LERE_DISMISS_ALL_STICKY_ABANDONED_CART,
  LERE_REMOVE_ABANDONED_CART,
  LERE_REMOVE_HIGH_INTENT,
  LERE_RESET_PERSONALISED_PREFERENCES,
  LERE_SAVE_ABANDONED_CART,
  LERE_SAVE_HOT_LEADS,
  OFFER_VIEWED,
  SET_OFFER_CHECK_IN_CHECK_OUT,
  SET_OFFER_LERE_INLINE_YMAL_IN_VIEW,
} from 'actions/actionConstants'
import {
  FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
  FETCH_LERE_HIGH_INTENT,
  FETCH_LERE_OFFER_STATS,
  FETCH_LERE_PERSONALISED_ALTERNATIVES,
  FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
  FETCH_LERE_PERSONALISED_PREFERENCES,
  FETCH_LERE_TOP_DEALS,
  FETCH_LERE_TOUR_RECOMMENDATIONS,
  FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
  USER_LOGOUT,
  FETCH_LERE_REGION_RECOMMENDATIONS,
  FETCH_URGENT_OFFER_ROOM_DATES,
  FETCH_LERE_USER_INTERACTIONS,
  FETCH_LATEST_RESTORABLE_CHECKOUT_CART,
} from 'actions/apiActionConstants'
import dedupeConcat from 'lib/array/arrayConcatDedupe'
import { partitionBy, sortBy, uniqueBy } from 'lib/array/arrayUtils'
import { isAbandonedCart, isRecentlyViewed } from 'lib/offer/highIntentOffers'
import { apiReducerSwitch, createAppReducer } from 'lib/redux/reducerUtils'

export const initialRecommendationState: App.RecommendationState = {
  calendarRecommendations: {
    state: 'loading',
    offerIds: [],
  },
  recommendations: {},
  offerSharedState: {
  },
  personalisedPeopleLikeMe: {
    offers: [],
    state: 'initial',
  },
  urgentOfferRoomDates: {
    fetching: false,
    data: {},
  },
  topDeals: {
    offerIds: [],
    state: 'loading',
    error: undefined,
    lereVersion: undefined,
  },
  highIntent: {
    offers: [],
    state: 'initial',
  },
  personalisedAlternatives: {
    offers: [],
    state: 'initial',
  },
  personalisedPreferences: {
    offers: [],
    state: 'initial',
  },
  offerStats: {},
  tour: {},
  regionRecommendations: {
    offers: [],
    state: 'loading',
    error: undefined,
    lereVersion: undefined,
  },
  userInteractions: {},
  latestRestorableCheckoutCart: {
    state: 'initial',
  },
}

const apiCalls = apiReducerSwitch<App.RecommendationState>({
  [FETCH_CALENDAR_OFFER_RECOMMENDATIONS]: {
    request: () => ({
      calendarRecommendations: {
        state: 'loading',
        offerIds: [],
      },
    }),
    success: (_, action) => {
      return ({
        calendarRecommendations: {
          state: 'done',
          offerIds: action.data.offerIds,
        },
      })
    },
    failure: (_, action) => {
      return ({
        calendarRecommendations: {
          state: 'error',
          error: action.error,
          offerIds: [],
        },
      })
    },
  },
  [FETCH_URGENT_OFFER_ROOM_DATES]: {
    request: (state) => ({
      urgentOfferRoomDates: {
        ...state.urgentOfferRoomDates,
        fetching: true,
      },
    }),
    success: (state, action) => ({
      urgentOfferRoomDates: {
        data: {
          ...state.urgentOfferRoomDates.data,
          [action.cacheKey]: action.data,
        },
        fetching: false,
      },
    }),
    failure: (state, action) => ({
      urgentOfferRoomDates: {
        ...state.urgentOfferRoomDates,
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_OFFER_BASED_LERE_RECOMMENDATIONS]: {
    request: (state, action) => {
      if (state.recommendations[action.cacheKey]?.filterKey === action.filterKey) {
        return state
      }
      return {
        ...state,
        recommendations: {
          ...state.recommendations,
          [action.cacheKey]: {
            offerIds: [],
            state: 'loading',
            filterKey: action.filterKey,
          },
        },
      }
    },
    success: (state, action) => {
      const offerIds = action.data
      const { cacheKey, filterKey } = action
      return ({
        ...state,
        recommendations: {
          ...state.recommendations,
          [cacheKey]: {
            offerIds,
            state: 'done',
            filterKey,
          },
        },
      })
    },
    failure: (state, action) => ({
      ...state,
      recommendations: {
        ...state.recommendations,
        [action.cacheKey]: {
          offerIds: [],
          state: 'error',
          error: action.error,
          filterKey: action.filterKey,
        },
      },
    }),
  },
  [FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME]: {
    request: () => ({
      personalisedPeopleLikeMe: {
        offers: [],
        state: 'loading',
        error: undefined,
        lereVersion: undefined,
      },
    }),
    success: (_, action) => ({
      personalisedPeopleLikeMe: {
        offers: action.data.offers,
        state: 'done',
        error: null,
        lereVersion: action.data.lereVersion,
      },
    }),
    failure: (state, action) => ({
      personalisedPeopleLikeMe: {
        ...state.personalisedPeopleLikeMe,
        state: 'error',
        error: action.error,
        lereVersion: undefined,
      },
    }),
  },
  [FETCH_LERE_TOP_DEALS]: {
    request: (state) => ({
      ...state,
      topDeals: {
        offerIds: [],
        state: 'loading',
        error: undefined,
        lereVersion: undefined,
      },
    }),
    success: (state, action) => ({
      ...state,
      topDeals: {
        offerIds: action.data.offerIds,
        state: 'done',
        error: null,
        lereVersion: action.data.lereVersion,
      },
    }),
    failure: (state, action) => ({
      ...state,
      topDeals: {
        offerIds: [],
        state: 'error',
        error: action.error,
        lereVersion: undefined,
      },
    }),
  },
  [FETCH_LERE_HIGH_INTENT]: {
    request: (state) => ({
      ...state,
      highIntent: {
        ...state.highIntent,
        state: 'loading',
        error: undefined,
      },
    }),
    success: (state, action) => {
      // we want to merge the current offers in state and the results from LERE
      const newOffers = dedupeConcat(action.data.offers, state.highIntent.offers, (offer: App.HighIntentOffer) => offer.offerId)
      const sortedNewOffers = sortBy(newOffers, (offer: App.HighIntentOffer) => offer.creationTime, 'desc')
      return ({
        ...state,
        highIntent: {
          ...state.highIntent,
          offers: sortedNewOffers,
          state: 'done',
          error: null,
          lereVersion: action.data.lereVersion,
        },
      })
    },
    failure: (state, action) => (
      state.highIntent.state === 'done' ? state : {
        ...state,
        highIntent: {
          ...state.highIntent,
          state: 'error',
          error: action.error,
        },
      }
    ),
  },
  [FETCH_LERE_PERSONALISED_ALTERNATIVES]: {
    request: (state) => ({
      personalisedAlternatives: {
        ...state.personalisedAlternatives,
        state: 'loading',
        error: undefined,
      },
    }),
    success: (_, action) => ({
      personalisedAlternatives: {
        offers: action.data.offers,
        state: 'done',
        error: null,
        lereVersion: action.data.lereVersion,
      },
    }),
    failure: (state, action) => (
      state.personalisedAlternatives.state === 'done' ? state : {
        personalisedAlternatives: {
          ...state.personalisedAlternatives,
          state: 'error',
          error: action.error,
        },
      }
    ),
  },
  [FETCH_LERE_PERSONALISED_PREFERENCES]: {
    request: (state) => ({
      personalisedPreferences: {
        ...state.personalisedPreferences,
        state: 'loading',
        error: undefined,
      },
    }),
    success: (_, action) => ({
      personalisedPreferences: {
        offers: action.data.offers,
        state: 'done',
        error: null,
        lereVersion: action.data.lereVersion,
      },
    }),
    failure: (state, action) => (
      state.personalisedPreferences.state === 'done' ? state : {
        personalisedPreferences: {
          ...state.personalisedPreferences,
          state: 'error',
          error: action.error,
        },
      }
    ),
  },
  [FETCH_LERE_OFFER_STATS]: {
    request: (state, action) => ({
      offerStats: {
        ...state.offerStats,
        [action.offerKey]: {
          stats: undefined,
          state: 'loading',
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      offerStats: {
        ...state.offerStats,
        [action.offerKey]: {
          stats: action.data.offerStats,
          state: 'done',
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      offerStats: {
        ...state.offerStats,
        [action.offerKey]: {
          stats: undefined,
          state: 'error',
          error: action.error,
        },
      },
    }),
  },
  [FETCH_LERE_TOUR_RECOMMENDATIONS]: {
    request: (state, action) => ({
      tour: {
        ...state.tour,
        [action.key]: {
          offers: [],
          state: 'loading',
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      tour: {
        ...state.tour,
        [action.key]: {
          offers: action.data.offers,
          state: 'done',
          error: undefined,
          lereVersion: action.data.lereVersion,
        },
      },
    }),
    failure: (state, action) => ({
      tour: {
        ...state.tour,
        [action.key]: {
          offers: [],
          state: 'error',
          error: action.error,
        },
      },
    }),
  },
  [FETCH_LERE_REGION_RECOMMENDATIONS]: {
    request: (state, action) => ({ ...state, [action.key]: { offers: [], state: 'loading', error: undefined, lereVersion: undefined } }),
    success: (state, action) => ({ ...state, [action.key]: { offers: action.data.offers, state: 'done', error: undefined, lereVersion: action.data.lereVersion } }),
    failure: (state, action) => ({ ...state, [action.key]: { offers: [], state: 'error', error: action.error, lereVersion: undefined } }),
  },
  [FETCH_LERE_USER_INTERACTIONS]: {
    request: (state, action) => ({ ...state, userInteractions: { ...state.userInteractions, [action.interactionType]: { interactions: [], state: 'loading', error: undefined } } }),
    success: (state, action) => ({ ...state, userInteractions: { ...state.userInteractions, [action.interactionType]: { interactions: action.data.interactions, state: 'done', error: undefined } } }),
    failure: (state, action) => ({ ...state, userInteractions: { ...state.userInteractions, [action.interactionType]: { interactions: [], state: 'error', error: action.error } } }),
  },
  [FETCH_LATEST_RESTORABLE_CHECKOUT_CART]: {
    request: (_, action) => ({
      latestRestorableCheckoutCart: {
        cart: undefined,
        state: 'loading',
        error: undefined,
        cacheKey: action.cacheKey,
      },
    }),
    success: (_, action) => ({
      latestRestorableCheckoutCart: {
        cart: action.data.cart,
        state: 'done',
        error: undefined,
        cacheKey: action.cacheKey,
      },
    }),
    failure: (_, action) => ({
      latestRestorableCheckoutCart: {
        cart: undefined,
        state: 'error',
        error: action.error,
        cacheKey: action.cacheKey,
      },
    }),
  },
  [USER_LOGOUT]: {
    success: () => ({ // clear personal recos when user logout
      highIntent: initialRecommendationState.highIntent,
      latestRestorableCheckoutCart: initialRecommendationState.latestRestorableCheckoutCart,
    }),
  },
})

const recommendationReducer = createAppReducer<App.RecommendationState>(initialRecommendationState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [SET_OFFER_LERE_INLINE_YMAL_IN_VIEW]: (state, action) => {
    const offerId = action.payload.offerId
    return {
      ...state,
      offerSharedState: {
        [offerId]: {
          ymalInlineInView: action.payload.ymalInlineInView,
          checkIn: state.offerSharedState[offerId]?.checkIn,
          checkOut: state.offerSharedState[offerId]?.checkOut,
          duration: state.offerSharedState[offerId]?.duration,
          rooms: state.offerSharedState[offerId]?.rooms,
        },
      },
    }
  },
  [SET_OFFER_CHECK_IN_CHECK_OUT]: (state, action) => {
    const offerId = action.payload.offerId
    return {
      ...state,
      offerSharedState: {
        [offerId]: {
          ymalInlineInView: state.offerSharedState[offerId]?.ymalInlineInView ?? false,
          checkIn: action.payload.checkIn,
          checkOut: action.payload.checkOut,
          duration: action.payload.duration,
          rooms: action.payload.rooms,
        },
      },
    }
  },
  [CLEAR_OFFER_CHECK_IN_CHECK_OUT]: (state, action) => {
    const updatedState: App.RecommendationState = {
      ...state,
      offerSharedState: {
        ...state.offerSharedState,
      },
    }
    delete updatedState.offerSharedState[action.payload.offerId]
    return updatedState
  },
  // save recently viewed
  [OFFER_VIEWED]: (state, action) => {
    const newView: App.RecentlyViewedOffer = action.view

    // only update RV if there is no other HI types
    // because RV is the lowerest priority
    let highIntentOffers = state.highIntent.offers
    const existingHighIntent = highIntentOffers.find(offer => offer.offerId === newView.offerId)
    if (!existingHighIntent || isRecentlyViewed(existingHighIntent)) {
      // order is maintained, dups are removed from the end
      highIntentOffers = uniqueBy([newView, ...highIntentOffers], offer => offer.offerId)
    }

    return {
      highIntent: {
        ...state.highIntent,
        lastViewed: newView,
        offers: highIntentOffers,
      },
    }
  },
  [LERE_SAVE_HOT_LEADS]: (state, action) => {
    const existingHighIntent = state.highIntent.offers.find(offer => offer.offerId === action.hotLead.offerId)

    // only update HL if no existing AC
    if (!existingHighIntent || !isAbandonedCart(existingHighIntent)) {
      return {
        highIntent: {
          ...state.highIntent,
          offers: uniqueBy([action.hotLead, ...state.highIntent.offers], (offer) => offer.offerId),
        },
      }
    }
    return state
  },
  [LERE_SAVE_ABANDONED_CART]: (state, action) => {
    // only keep one item per main offer id
    const offers: Array<App.HighIntentOffer> = uniqueBy(
      [action.cart, ...state.highIntent.offers],
      (offer: App.HighIntentOffer) => offer.offerId,
    )

    return {
      highIntent: {
        ...state.highIntent,
        offers,
      },
    }
  },
  [LERE_REMOVE_HIGH_INTENT]: (state, action) => {
    return {
      highIntent: {
        ...state.highIntent,
        offers: state.highIntent.offers.filter(
          offer => !(action.offerIds.includes(offer.offerId) && action.highIntentCategories.includes(offer.category)),
        ),
      },
    }
  },
  [LERE_DISMISS_ALL_STICKY_ABANDONED_CART]: (state) => {
    const [carts, rest] = partitionBy(state.highIntent.offers, isAbandonedCart)

    if (carts.length) {
      const newCarts = carts.map(cart => ({
        ...cart,
        isDismissed: true,
      }))
      return {
        highIntent: {
          ...state.highIntent,
          offers: [...newCarts, ...rest],
        },
      }
    }

    return state
  },
  [LERE_REMOVE_ABANDONED_CART]: (state, action) => {
    const idsToRemove: Array<string> = action.offerIds
    const newOffers = state.highIntent.offers.filter(offer => !idsToRemove.includes(offer.offerId))
    return {
      highIntent: {
        ...state.highIntent,
        offers: newOffers,
      },
    }
  },
  [LERE_RESET_PERSONALISED_PREFERENCES]: () => ({
    personalisedPreferences: {
      offers: [],
      state: 'loading',
    },
  }),
})

export default recommendationReducer
